import React, { useEffect, useState, useCallback } from 'react';
import { db, storage } from '../firebase/config.js';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { ref, listAll, getDownloadURL, getMetadata } from 'firebase/storage';
import { parseISO, format, isAfter, isBefore, addDays, startOfDay, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths, getDay } from 'date-fns';

// Room display utility functions
const getRoomForPeriod = (jobData, periodId) => {
  // Check if this job uses multiple rooms
  if (jobData.useMultipleRooms && jobData.periodRooms) {
    return jobData.periodRooms[periodId] || 'Room not specified';
  } else {
    // Use the single room field
    return jobData.room || 'N/A';
  }
};

const getRoomSummary = (jobData) => {
  if (jobData.useMultipleRooms && jobData.periodRooms) {
    const roomEntries = Object.entries(jobData.periodRooms);
    if (roomEntries.length > 0) {
      return roomEntries.map(([periodId, room]) => `${periodId}: ${room}`).join(', ');
    }
    return 'Multiple rooms (see periods)';
  } else {
    return jobData.room || 'N/A';
  }
};

const usesMultipleRooms = (jobData) => {
  return Boolean(jobData.useMultipleRooms && jobData.periodRooms);
};

// Modal Component for Sub Plan Details
const SubPlanModal = ({ isOpen, onClose, subRequest, currentUser }) => {
  const [subPlanDetails, setSubPlanDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && subRequest) {
      fetchSubPlanDetails();
    }
  }, [isOpen, subRequest]);

  const fetchSubPlanDetails = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('SubRequest data:', subRequest); // Debug log
      
      // Extract all potentially useful information from the sub request document
      const extractedData = {
        // Basic instruction fields
        instructions: subRequest.instructions || subRequest.subPlan?.instructions || subRequest.notes || subRequest.description,
        emergencyProcedures: subRequest.emergencyProcedures || subRequest.subPlan?.emergencyProcedures || subRequest.emergency || subRequest.emergencyInfo,
        additionalNotes: subRequest.additionalNotes || subRequest.subPlan?.additionalNotes || subRequest.comments || subRequest.specialInstructions,
        
        // Period-specific details - this is the key field!
        periodDetails: subRequest.periodNotes || subRequest.periodDetails || subRequest.subPlan?.periodDetails,
        
        // File attachments - check multiple possible field names
        attachments: subRequest.attachments || subRequest.subPlan?.attachments || subRequest.files || subRequest.uploadedFiles || subRequest.fileAttachments || [],
        
        // Contact information
        contactEmail: subRequest.contactEmail || subRequest.subPlan?.contactEmail || subRequest.requesterEmail,
        contactPhone: subRequest.contactPhone || subRequest.subPlan?.contactPhone || subRequest.phone,
        
        // Absence reason and additional context
        reason: subRequest.reason || subRequest.absenceReason,
        subType: subRequest.subType || subRequest.type,
        
        // Sharing and notification information
        shareWith: subRequest.shareWith || {},
        notificationSent: subRequest.notificationSent,
        preferredSubs: subRequest.preferredSubs || [],
        
        // Department and organizational info
        departments: subRequest.departments || [],
        allFaculty: subRequest.allFaculty,
        
        // Timing information
        submittedByPrivilegedUserId: subRequest.submittedByPrivilegedUserId,
        submittedByPrivilegedUserName: subRequest.submittedByPrivilegedUserName,
        notificationTimestamp: subRequest.notificationTimestamp,
        
        // Any other relevant fields
        classroomManagement: subRequest.classroomManagement,
        studentBehaviorNotes: subRequest.studentBehaviorNotes,
        scheduleNotes: subRequest.scheduleNotes,
        equipmentNotes: subRequest.equipmentNotes,
        
        // Raw data for debugging
        allFields: Object.keys(subRequest).filter(key => 
          !['id', 'name', 'date', 'periods', 'filled', 'tenantId', 'requesterUid', 'department', 'room', 'dateObj'].includes(key)
        ).reduce((acc, key) => {
          acc[key] = subRequest[key];
          return acc;
        }, {})
      };
      
      // Fetch files from Firebase Storage
      try {
        console.log('Fetching files from Firebase Storage for request:', subRequest.id);
        const storageRef = ref(storage, `sub_files/${subRequest.tenantId}/${subRequest.id}`);
        const fileList = await listAll(storageRef);
        
        if (fileList.items.length > 0) {
          console.log(`Found ${fileList.items.length} files in storage`);
          const storageFiles = await Promise.all(
            fileList.items.map(async (fileRef) => {
              try {
                const url = await getDownloadURL(fileRef);
                const metadata = await getMetadata(fileRef);
                return {
                  name: fileRef.name,
                  url: url,
                  size: metadata.size,
                  type: metadata.contentType,
                  timeCreated: metadata.timeCreated,
                  source: 'firebase_storage'
                };
              } catch (error) {
                console.error('Error getting file details:', error);
                return {
                  name: fileRef.name,
                  error: 'Could not load file details',
                  source: 'firebase_storage'
                };
              }
            })
          );
          
          // Add storage files to attachments
          extractedData.attachments = [...(extractedData.attachments || []), ...storageFiles];
          console.log('Combined attachments:', extractedData.attachments);
        } else {
          console.log('No files found in storage for this request');
        }
      } catch (storageError) {
        console.error('Error fetching files from storage:', storageError);
        extractedData.storageError = storageError.message;
      }
      
      console.log('Extracted sub plan data:', extractedData);
      
      // Check if we have any meaningful content - prioritize periodDetails/periodNotes
      const hasContent = !!(
        (extractedData.periodDetails && Object.keys(extractedData.periodDetails).length > 0) ||
        extractedData.instructions ||
        extractedData.emergencyProcedures ||
        extractedData.additionalNotes ||
        extractedData.reason ||
        (extractedData.attachments && extractedData.attachments.length > 0) ||
        extractedData.classroomManagement ||
        extractedData.studentBehaviorNotes ||
        extractedData.scheduleNotes ||
        extractedData.equipmentNotes ||
        (extractedData.shareWith && Object.keys(extractedData.shareWith).length > 0) ||
        (extractedData.preferredSubs && extractedData.preferredSubs.length > 0) ||
        extractedData.submittedByPrivilegedUserName ||
        extractedData.notificationSent !== undefined
      );
      
      if (hasContent) {
        setSubPlanDetails(extractedData);
      } else {
        // Show all available fields for debugging
        const availableFields = Object.keys(subRequest).map(key => ({
          key,
          value: subRequest[key],
          type: typeof subRequest[key]
        }));
        
        setError(`No sub plan content found in this request. Available fields: ${JSON.stringify(availableFields, null, 2)}`);
      }
      
    } catch (err) {
      console.error('Error processing sub request details:', err);
      setError('Failed to load sub plan details: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = async (fileUrl, fileName) => {
  try {
    // For Firebase Storage URLs, we need to handle CORS differently
    // Instead of using fetch, we'll create a link that opens in a new tab
    // which bypasses CORS restrictions
    
    if (fileUrl.includes('firebasestorage.googleapis.com')) {
      // For Firebase Storage files, open in new tab to download
      const link = document.createElement('a');
      link.href = fileUrl;
      link.target = '_blank';
      link.download = fileName;
      
      // Add the link to the document, click it, then remove it
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
    } else {
      // For other URLs, try the original fetch method
      const response = await fetch(fileUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }
    
  } catch (error) {
    console.error('Error downloading file:', error);
    
    // Fallback: try opening the file in a new tab
    try {
      window.open(fileUrl, '_blank');
    } catch (fallbackError) {
      console.error('Fallback download also failed:', fallbackError);
      alert(`Failed to download file. You can try accessing it directly at: ${fileUrl}`);
    }
  }
};

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '24px',
        maxWidth: '800px',
        maxHeight: '80vh',
        overflow: 'auto',
        margin: '20px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          borderBottom: '2px solid #007bff',
          paddingBottom: '10px'
        }}>
          <h2 style={{ margin: 0, color: '#007bff' }}>
            Sub Plan Details - {subRequest.name}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#6c757d'
            }}
          >
            ×
          </button>
        </div>

        {/* Basic Request Info */}
        <div style={{
          backgroundColor: '#f8f9fa',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          <h3 style={{ marginTop: 0, marginBottom: '10px' }}>Request Information</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div><strong>Teacher:</strong> {subRequest.name}</div>
            <div><strong>Department:</strong> {subRequest.department || 'N/A'}</div>
            <div><strong>Room(s):</strong> {getRoomSummary(subRequest)}</div>
            <div><strong>Date:</strong> {subRequest.dateObj ? format(subRequest.dateObj, 'EEEE, MMMM d, yyyy') : 'N/A'}</div>
            <div><strong>Periods:</strong> {subRequest.periods ? subRequest.periods.join(', ') : 'N/A'}</div>
          </div>
        </div>

        {loading && (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            Loading sub plan details...
          </div>
        )}

        {error && (
          <div style={{
            backgroundColor: '#f8d7da',
            color: '#721c24',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            {error}
          </div>
        )}

        {subPlanDetails && (
          <div>
            {/* Sub Plan Instructions */}
            {subPlanDetails.instructions && (
              <div style={{ marginBottom: '20px' }}>
                <h3>Instructions for Substitute</h3>
                <div style={{
                  backgroundColor: '#fff',
                  border: '1px solid #dee2e6',
                  borderRadius: '8px',
                  padding: '15px',
                  whiteSpace: 'pre-wrap'
                }}>
                  {subPlanDetails.instructions}
                </div>
              </div>
            )}

            {/* Absence Reason */}
            {subPlanDetails.reason && (
              <div style={{ marginBottom: '20px' }}>
                <h3>Absence Reason</h3>
                <div style={{
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #dee2e6',
                  borderRadius: '8px',
                  padding: '15px'
                }}>
                  {subPlanDetails.reason}
                </div>
              </div>
            )}

            {/* Emergency Procedures */}
            {subPlanDetails.emergencyProcedures && (
              <div style={{ marginBottom: '20px' }}>
                <h3>Emergency Procedures</h3>
                <div style={{
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffeaa7',
                  borderRadius: '8px',
                  padding: '15px',
                  whiteSpace: 'pre-wrap'
                }}>
                  {subPlanDetails.emergencyProcedures}
                </div>
              </div>
            )}

            {/* Additional Notes */}
            {subPlanDetails.additionalNotes && (
              <div style={{ marginBottom: '20px' }}>
                <h3>Additional Notes</h3>
                <div style={{
                  backgroundColor: '#fff',
                  border: '1px solid #dee2e6',
                  borderRadius: '8px',
                  padding: '15px',
                  whiteSpace: 'pre-wrap'
                }}>
                  {subPlanDetails.additionalNotes}
                </div>
              </div>
            )}

            {/* Classroom Management */}
            {subPlanDetails.classroomManagement && (
              <div style={{ marginBottom: '20px' }}>
                <h3>Classroom Management</h3>
                <div style={{
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #dee2e6',
                  borderRadius: '8px',
                  padding: '15px',
                  whiteSpace: 'pre-wrap'
                }}>
                  {subPlanDetails.classroomManagement}
                </div>
              </div>
            )}

            {/* Student Behavior Notes */}
            {subPlanDetails.studentBehaviorNotes && (
              <div style={{ marginBottom: '20px' }}>
                <h3>Student Behavior Notes</h3>
                <div style={{
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffeaa7',
                  borderRadius: '8px',
                  padding: '15px',
                  whiteSpace: 'pre-wrap'
                }}>
                  {subPlanDetails.studentBehaviorNotes}
                </div>
              </div>
            )}

            {/* Schedule Notes */}
            {subPlanDetails.scheduleNotes && (
              <div style={{ marginBottom: '20px' }}>
                <h3>Schedule Notes</h3>
                <div style={{
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #dee2e6',
                  borderRadius: '8px',
                  padding: '15px',
                  whiteSpace: 'pre-wrap'
                }}>
                  {subPlanDetails.scheduleNotes}
                </div>
              </div>
            )}

            {/* Equipment Notes */}
            {subPlanDetails.equipmentNotes && (
              <div style={{ marginBottom: '20px' }}>
                <h3>Equipment Notes</h3>
                <div style={{
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #dee2e6',
                  borderRadius: '8px',
                  padding: '15px',
                  whiteSpace: 'pre-wrap'
                }}>
                  {subPlanDetails.equipmentNotes}
                </div>
              </div>
            )}

            {/* Class Schedule/Period Details - THE MOST IMPORTANT SECTION */}
            {subPlanDetails.periodDetails && Object.keys(subPlanDetails.periodDetails).length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ color: '#dc3545', fontWeight: 'bold' }}>📋 Period Instructions (Main Sub Plans)</h3>
                {Object.entries(subPlanDetails.periodDetails).map(([period, details]) => (
                  <div key={period} style={{
                    backgroundColor: '#fff3cd',
                    border: '2px solid #ffc107',
                    borderRadius: '8px',
                    padding: '15px',
                    marginBottom: '10px'
                  }}>
                    <h4 style={{ marginTop: 0, color: '#856404', fontWeight: 'bold' }}>
                      📚 Period {period} Instructions
                      {/* Show room for this period if using multiple rooms */}
                      {usesMultipleRooms(subRequest) && (
                        <span style={{ fontSize: '0.8em', color: '#6c757d', marginLeft: '10px' }}>
                          (Room: {getRoomForPeriod(subRequest, period)})
                        </span>
                      )}
                    </h4>
                    {typeof details === 'string' ? (
                      <div style={{ 
                        whiteSpace: 'pre-wrap',
                        fontSize: '16px',
                        fontWeight: '500',
                        color: '#495057'
                      }}>
                        {details}
                      </div>
                    ) : (
                      <div>
                        {details.subject && <p><strong>Subject:</strong> {details.subject}</p>}
                        {details.time && <p><strong>Time:</strong> {details.time}</p>}
                        {details.instructions && (
                          <div style={{ whiteSpace: 'pre-wrap', fontSize: '16px', fontWeight: '500' }}>
                            <strong>Instructions:</strong><br />
                            {details.instructions}
                          </div>
                        )}
                        {typeof details === 'object' && !details.instructions && !details.subject && !details.time && (
                          <div style={{ whiteSpace: 'pre-wrap', fontSize: '16px', fontWeight: '500' }}>
                            {JSON.stringify(details, null, 2)}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* File Attachments - Enhanced with Firebase Storage support */}
            <div style={{ marginBottom: '20px' }}>
              <h3>📎 Attached Files</h3>
              <div style={{
                backgroundColor: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '8px',
                padding: '15px'
              }}>
                {(() => {
                  const foundFiles = subPlanDetails.attachments || [];
                  
                  // Display files if found
                  if (foundFiles.length > 0) {
                    return (
                      <div>
                        <div style={{ marginBottom: '10px', fontSize: '0.9em', color: '#28a745' }}>
                          ✅ Found {foundFiles.length} file(s)
                        </div>
                        {foundFiles.map((file, index) => {
                          const fileName = file.name || file.filename || file.originalName || 'Unknown file';
                          const fileSize = file.size ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : 'Unknown size';
                          const fileType = file.type || file.contentType || 'Unknown type';
                          const source = file.source || 'database';
                          
                          return (
                            <div key={index} style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '12px',
                              backgroundColor: 'white',
                              border: '1px solid #dee2e6',
                              borderRadius: '4px',
                              marginBottom: '8px'
                            }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{fileName}</div>
                                <div style={{ fontSize: '0.8em', color: '#6c757d' }}>
                                  <span>Size: {fileSize}</span>
                                  {fileType && <span> | Type: {fileType}</span>}
                                  <span> | Source: {source === 'firebase_storage' ? '🔥 Firebase Storage' : '📄 Database'}</span>
                                </div>
                                {file.timeCreated && (
                                  <div style={{ fontSize: '0.8em', color: '#999' }}>
                                    Uploaded: {new Date(file.timeCreated).toLocaleString()}
                                  </div>
                                )}
                                {file.error && (
                                  <div style={{ fontSize: '0.8em', color: '#dc3545' }}>
                                    Error: {file.error}
                                  </div>
                                )}
                              </div>
                              {file.url && !file.error ? (
                                <button
                                  onClick={() => downloadFile(file.url, fileName)}
                                  style={{
                                    backgroundColor: '#007bff',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    padding: '8px 16px',
                                    cursor: 'pointer',
                                    marginLeft: '10px'
                                  }}
                                >
                                  Download
                                </button>
                              ) : (
                                <span style={{ color: '#dc3545', fontSize: '0.8em', marginLeft: '10px' }}>
                                  {file.error ? 'Error' : 'No URL'}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  }
                  
                  // Show debug info if no files found
                  return (
                    <div>
                      <div style={{
                        textAlign: 'center',
                        padding: '20px',
                        color: '#6c757d',
                        fontStyle: 'italic'
                      }}>
                        📄 No files attached to this sub request
                      </div>
                      
                      {subPlanDetails.storageError && (
                        <div style={{
                          backgroundColor: '#f8d7da',
                          color: '#721c24',
                          padding: '10px',
                          borderRadius: '4px',
                          marginTop: '10px',
                          fontSize: '0.9em'
                        }}>
                          <strong>Storage Error:</strong> {subPlanDetails.storageError}
                        </div>
                      )}
                      
                      <details style={{ marginTop: '10px' }}>
                        <summary style={{ cursor: 'pointer', fontSize: '0.8em', color: '#999' }}>
                          🔍 Debug: File search details (Click to expand)
                        </summary>
                        <div style={{ 
                          fontSize: '0.7em', 
                          backgroundColor: '#f1f1f1',
                          padding: '8px',
                          borderRadius: '4px',
                          marginTop: '5px'
                        }}>
                          <div><strong>Request ID:</strong> {subRequest.id}</div>
                          <div><strong>Tenant ID:</strong> {subRequest.tenantId}</div>
                          <div><strong>Storage Path:</strong> sub_files/{subRequest.tenantId}/{subRequest.id}</div>
                          <div><strong>Database fields checked:</strong> attachments, files, uploadedFiles, fileAttachments</div>
                          <div><strong>Available fields:</strong> {Object.keys(subPlanDetails.allFields || {}).join(', ')}</div>
                        </div>
                      </details>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Who's Assigned/Signed Up - Fixed to show actual notification recipients */}
            <div style={{ marginBottom: '20px' }}>
              <h3>👥 Notification Recipients</h3>
              <div style={{
                backgroundColor: '#e7f3ff',
                border: '1px solid #b3d7ff',
                borderRadius: '8px',
                padding: '15px'
              }}>
                {/* Show actual recipient count and targeting info */}
                <div style={{ 
                  fontSize: '1.1em', 
                  fontWeight: 'bold',
                  color: '#0066cc',
                  marginBottom: '15px'
                }}>
                  📢 Notification Summary
                </div>
                
                <div style={{ 
                  backgroundColor: 'white',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                  padding: '12px',
                  marginBottom: '15px'
                }}>
                  <div style={{ fontSize: '1em', marginBottom: '8px' }}>
                    <strong>📊 Total Recipients:</strong> {
                      subPlanDetails.shareWith?.recipientCount !== undefined 
                        ? subPlanDetails.shareWith.recipientCount 
                        : (subPlanDetails.shareWith?.preferredSubs?.length > 0 
                            ? subPlanDetails.shareWith.preferredSubs.length 
                            : 'No notifications sent')
                    }
                  </div>
                  
                  {subPlanDetails.allFaculty !== undefined && (
                    <div style={{ fontSize: '0.9em', marginBottom: '6px' }}>
                      <strong>👥 Sent to All Faculty:</strong> {subPlanDetails.allFaculty ? '✅ Yes' : '❌ No'}
                    </div>
                  )}
                  
                  {subPlanDetails.departments && subPlanDetails.departments.length > 0 ? (
                    <div style={{ fontSize: '0.9em', marginBottom: '6px' }}>
                      <strong>🏢 Target Departments:</strong> {subPlanDetails.departments.join(', ')}
                    </div>
                  ) : (
                    <div style={{ fontSize: '0.9em', marginBottom: '6px' }}>
                      <strong>🏢 Target Departments:</strong> All departments
                    </div>
                  )}
                  
                  {subPlanDetails.notificationSent !== undefined && (
                    <div style={{ fontSize: '0.9em', marginBottom: '6px' }}>
                      <strong>📤 Notification Status:</strong> {subPlanDetails.notificationSent ? '✅ Sent' : '❌ Not sent'}
                    </div>
                  )}
                  
                  {subPlanDetails.notificationTimestamp && (
                    <div style={{ fontSize: '0.9em' }}>
                      <strong>⏰ Sent At:</strong> {new Date(subPlanDetails.notificationTimestamp.seconds * 1000).toLocaleString()}
                    </div>
                  )}
                </div>

                {/* Show the actual people who were notified */}
                {subPlanDetails.shareWith?.preferredSubs && subPlanDetails.shareWith.preferredSubs.length > 0 ? (
                  <div style={{ marginTop: '15px' }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '10px', color: '#28a745' }}>
                      📧 People Who Received Notifications:
                    </div>
                    {subPlanDetails.shareWith.preferredSubs.map((recipient, index) => {
                      const name = recipient.name || 
                                  (recipient.firstName && recipient.lastName ? `${recipient.firstName} ${recipient.lastName}` : '') ||
                                  recipient.lastName ||
                                  'Unknown User';
                      
                      const email = recipient.email || 'No email provided';
                      const role = recipient.role || recipient.title || 'No role specified';
                      const department = recipient.department || 'N/A';
                      const phone = recipient.phone || 'N/A';
                      
                      return (
                        <div key={index} style={{
                          backgroundColor: 'white',
                          border: '1px solid #28a745',
                          borderRadius: '4px',
                          padding: '12px',
                          marginBottom: '8px'
                        }}>
                          <div style={{ fontWeight: 'bold', fontSize: '16px', color: '#2c3e50' }}>
                            {name}
                          </div>
                          <div style={{ fontSize: '0.9em', color: '#6c757d', marginTop: '4px' }}>
                            <strong>📧 Email:</strong> {email}
                          </div>
                          <div style={{ fontSize: '0.9em', color: '#6c757d' }}>
                            <strong>👔 Role:</strong> {role} | <strong>🏢 Department:</strong> {department}
                          </div>
                          {phone !== 'N/A' && (
                            <div style={{ fontSize: '0.9em', color: '#6c757d' }}>
                              <strong>📞 Phone:</strong> {phone}
                            </div>
                          )}
                          {recipient.sis_id && (
                            <div style={{ fontSize: '0.8em', color: '#999' }}>
                              SIS ID: {recipient.sis_id}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ 
                    marginTop: '15px',
                    padding: '15px',
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffeaa7',
                    borderRadius: '4px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '1.1em', fontWeight: 'bold', color: '#856404', marginBottom: '5px' }}>
                      📭 No Notification Recipients Found
                    </div>
                    <div style={{ fontSize: '0.9em', color: '#6c757d' }}>
                      This request may not have been sent to any specific individuals yet.
                    </div>
                  </div>
                )}

                {/* Show who else is available (general preferredSubs list) if different */}
                {subPlanDetails.preferredSubs && subPlanDetails.preferredSubs.length > 0 && (
                  <div style={{ marginTop: '15px' }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '10px', color: '#007bff' }}>
                      🎯 Available Substitute Pool ({subPlanDetails.preferredSubs.length} people):
                    </div>
                    <details>
                      <summary style={{ cursor: 'pointer', color: '#007bff', marginBottom: '10px' }}>
                        Click to view all available substitutes
                      </summary>
                      {subPlanDetails.preferredSubs.map((sub, index) => {
                        const name = sub.name || 
                                    (sub.firstName && sub.lastName ? `${sub.firstName} ${sub.lastName}` : '') ||
                                    'Unknown User';
                        
                        return (
                          <div key={index} style={{
                            backgroundColor: '#f8f9fa',
                            border: '1px solid #dee2e6',
                            borderRadius: '4px',
                            padding: '8px',
                            marginBottom: '6px',
                            fontSize: '0.9em'
                          }}>
                            <strong>{name}</strong> ({sub.role || 'No role'}) - {sub.email || 'No email'}
                            {sub.payRate && <span style={{ color: '#28a745', fontWeight: 'bold' }}> - ${sub.payRate}/{sub.payType || 'day'}</span>}
                          </div>
                        );
                      })}
                    </details>
                  </div>
                )}
                
                {/* Raw notification data for debugging */}
                <details style={{ marginTop: '15px' }}>
                  <summary style={{ cursor: 'pointer', fontSize: '0.8em', color: '#999' }}>
                    🔍 Raw Notification Data (Click to expand)
                  </summary>
                  <pre style={{ 
                    fontSize: '0.7em', 
                    backgroundColor: '#f8f9fa',
                    padding: '8px',
                    borderRadius: '4px',
                    marginTop: '5px',
                    overflow: 'auto',
                    maxHeight: '200px'
                  }}>
                    {JSON.stringify({
                      recipientCount: subPlanDetails.shareWith?.recipientCount,
                      allFaculty: subPlanDetails.allFaculty,
                      departments: subPlanDetails.departments,
                      notificationSent: subPlanDetails.notificationSent,
                      notificationTimestamp: subPlanDetails.notificationTimestamp,
                      actualRecipients: subPlanDetails.shareWith?.preferredSubs,
                      generalPreferredSubs: subPlanDetails.preferredSubs?.length
                    }, null, 2)}
                  </pre>
                </details>
              </div>
            </div>

            {/* Request Submission Info */}
            {(subPlanDetails.submittedByPrivilegedUserName || subPlanDetails.notificationSent !== undefined) && (
              <div style={{ marginBottom: '20px' }}>
                <h3>📋 Request Details</h3>
                <div style={{
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #dee2e6',
                  borderRadius: '8px',
                  padding: '15px'
                }}>
                  {subPlanDetails.submittedByPrivilegedUserName && (
                    <p><strong>Submitted by:</strong> {subPlanDetails.submittedByPrivilegedUserName}</p>
                  )}
                  {subPlanDetails.notificationSent !== undefined && (
                    <p><strong>Notification sent:</strong> {subPlanDetails.notificationSent ? '✅ Yes' : '❌ No'}</p>
                  )}
                  {subPlanDetails.notificationTimestamp && (
                    <p><strong>Notification time:</strong> {new Date(subPlanDetails.notificationTimestamp.seconds * 1000).toLocaleString()}</p>
                  )}
                  {subPlanDetails.allFaculty !== undefined && (
                    <p><strong>Sent to all faculty:</strong> {subPlanDetails.allFaculty ? '✅ Yes' : '❌ No'}</p>
                  )}
                  {subPlanDetails.departments && subPlanDetails.departments.length > 0 && (
                    <p><strong>Target departments:</strong> {subPlanDetails.departments.join(', ')}</p>
                  )}
                </div>
              </div>
            )}

            {/* Contact Information */}
            {(subPlanDetails.contactEmail || subPlanDetails.contactPhone) && (
              <div style={{ marginBottom: '20px' }}>
                <h3>Contact Information</h3>
                <div style={{
                  backgroundColor: '#d1ecf1',
                  border: '1px solid #bee5eb',
                  borderRadius: '8px',
                  padding: '15px'
                }}>
                  {subPlanDetails.contactEmail && (
                    <p><strong>Email:</strong> {subPlanDetails.contactEmail}</p>
                  )}
                  {subPlanDetails.contactPhone && (
                    <p><strong>Phone:</strong> {subPlanDetails.contactPhone}</p>
                  )}
                </div>
              </div>
            )}

            {/* Debug: Show all available fields if in development - now less prominent */}
            {subPlanDetails.allFields && Object.keys(subPlanDetails.allFields).length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <details style={{ backgroundColor: '#f1f3f4', padding: '10px', borderRadius: '4px', fontSize: '12px' }}>
                  <summary style={{ cursor: 'pointer', fontSize: '11px', color: '#6c757d' }}>
                    🔍 Developer Debug: Raw Data (Click to expand)
                  </summary>
                  <pre style={{ 
                    marginTop: '10px', 
                    padding: '8px', 
                    backgroundColor: 'white', 
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    fontSize: '10px',
                    overflow: 'auto',
                    maxHeight: '200px'
                  }}>
                    {JSON.stringify(subPlanDetails.allFields, null, 2)}
                  </pre>
                </details>
              </div>
            )}
          </div>
        )}

        <div style={{ textAlign: 'right', marginTop: '20px' }}>
          <button
            onClick={onClose}
            style={{
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '10px 20px',
              cursor: 'pointer'
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Calendar Component for SubbingStatus
const SubbingStatusCalendar = ({ requests, onDateClick, selectedDate, currentUser, onRequestClick }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Get activities for a specific date
  const getActivitiesForDate = (date) => {
    const activities = [];
    
    requests.forEach(request => {
      if (request.dateObj && isSameDay(request.dateObj, date)) {
        request.periods?.forEach(period => {
          const subInfo = request.filled?.[period];
          let status = 'unfilled';
          let substituteInfo = null;
          let isCurrentUser = false;

          if (subInfo && typeof subInfo === 'object' && subInfo.name) {
            status = 'filled';
            substituteInfo = subInfo;
            isCurrentUser = subInfo.email === currentUser.email || subInfo.uid === currentUser.uid;
          } else if (typeof subInfo === 'string' && subInfo.trim() !== "") {
            status = 'filled';
            substituteInfo = { name: subInfo };
          }

          activities.push({
            period: period,
            status: status,
            teacher: request.name,
            department: request.department,
            room: getRoomForPeriod(request, period), // Use new room logic
            requestId: request.id,
            substituteInfo: substituteInfo,
            isCurrentUser: isCurrentUser,
            isMyRequest: request.requesterUid === currentUser.uid,
            fullRequest: request
          });
        });
      }
    });

    return activities.sort((a, b) => Number(a.period) - Number(b.period));
  };

  // Get the primary color for a date based on activities
  const getDateColor = (date) => {
    const activities = getActivitiesForDate(date);
    if (activities.length === 0) return 'white';
    
    // Priority: current user subbing (blue) > unfilled (red) > filled by others (green)
    const hasCurrentUserSubbing = activities.some(a => a.isCurrentUser);
    const hasUnfilled = activities.some(a => a.status === 'unfilled');
    const hasFilled = activities.some(a => a.status === 'filled');
    
    if (hasCurrentUserSubbing) return '#e3f2fd'; // Light blue
    if (hasUnfilled) return '#ffebee'; // Light red
    if (hasFilled) return '#e8f5e8'; // Light green
    
    return 'white';
  };

  // Generate calendar grid with proper week alignment
  const generateCalendarGrid = () => {
    const firstDayOfWeek = getDay(monthStart);
    const grid = [];
    
    // Add empty cells for days before the month starts
    for (let i = 0; i < firstDayOfWeek; i++) {
      grid.push(null);
    }
    
    // Add all days of the month
    days.forEach(day => {
      grid.push(day);
    });
    
    return grid;
  };

  const calendarGrid = generateCalendarGrid();

  const goToPreviousMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  return (
    <div style={{ 
      border: '1px solid #ddd', 
      borderRadius: '8px', 
      padding: '20px',
      backgroundColor: 'white',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
    }}>
      {/* Calendar Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '20px' 
      }}>
        <button 
          onClick={goToPreviousMonth}
          style={{
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            padding: '8px 12px',
            cursor: 'pointer'
          }}
        >
          ← Previous
        </button>
        <h3 style={{ margin: 0 }}>
          {format(currentMonth, 'MMMM yyyy')}
        </h3>
        <button 
          onClick={goToNextMonth}
          style={{
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            padding: '8px 12px',
            cursor: 'pointer'
          }}
        >
          Next →
        </button>
      </div>

      {/* Day Labels */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(7, 1fr)', 
        gap: '1px',
        marginBottom: '5px'
      }}>
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} style={{ 
            padding: '10px', 
            textAlign: 'center', 
            fontWeight: 'bold',
            backgroundColor: '#f8f9fa',
            border: '1px solid #dee2e6'
          }}>
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(7, 1fr)', 
        gap: '1px'
      }}>
        {calendarGrid.map((day, index) => {
          if (!day) {
            return (
              <div key={`empty-${index}`} style={{ 
                minHeight: '80px',
                border: '1px solid #dee2e6',
                backgroundColor: '#f8f9fa'
              }} />
            );
          }

          const activitiesForDay = getActivitiesForDate(day);
          const dateColor = getDateColor(day);
          const isSelected = selectedDate && isSameDay(day, selectedDate);
          const isToday = isSameDay(day, new Date());
          const isPastDay = isBefore(day, startOfDay(new Date()));

          return (
            <div
              key={day.toISOString()}
              onClick={() => onDateClick(day)}
              style={{
                minHeight: '80px',
                border: '2px solid ' + (isSelected ? '#007bff' : '#dee2e6'),
                padding: '5px',
                cursor: 'pointer',
                backgroundColor: dateColor,
                opacity: isPastDay ? 0.7 : 1,
                position: 'relative'
              }}
            >
              <div style={{ 
                fontWeight: isToday ? 'bold' : 'normal',
                color: isToday ? '#007bff' : isPastDay ? '#6c757d' : 'black',
                marginBottom: '3px'
              }}>
                {format(day, 'd')}
              </div>
              
              {/* Activity indicators */}
              {activitiesForDay.length > 0 && (
                <div style={{ fontSize: '10px' }}>
                  {activitiesForDay.slice(0, 3).map((activity, idx) => (
                    <div key={idx} style={{
                      backgroundColor: activity.isCurrentUser 
                        ? '#2196f3' 
                        : activity.status === 'unfilled' 
                          ? '#f44336' 
                          : '#4caf50',
                      color: 'white',
                      borderRadius: '8px',
                      padding: '1px 4px',
                      margin: '1px 0',
                      fontSize: '9px',
                      textAlign: 'center'
                    }}>
                      P{activity.period}
                    </div>
                  ))}
                  {activitiesForDay.length > 3 && (
                    <div style={{
                      fontSize: '8px',
                      color: '#6c757d',
                      textAlign: 'center'
                    }}>
                      +{activitiesForDay.length - 3} more
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div style={{ marginTop: '15px', fontSize: '12px' }}>
        <h4 style={{ margin: '0 0 10px 0' }}>Legend:</h4>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <div style={{ 
              width: '12px', 
              height: '12px', 
              backgroundColor: '#e8f5e8', 
              border: '1px solid #dee2e6' 
            }} />
            <span>All Periods Filled</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <div style={{ 
              width: '12px', 
              height: '12px', 
              backgroundColor: '#ffebee', 
              border: '1px solid #dee2e6' 
            }} />
            <span>Has Unfilled Periods</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <div style={{ 
              width: '12px', 
              height: '12px', 
              backgroundColor: '#e3f2fd', 
              border: '1px solid #dee2e6' 
            }} />
            <span>You're Subbing</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <div style={{ 
              width: '12px', 
              height: '12px', 
              backgroundColor: '#4caf50'
            }} />
            <span>P# = Filled Period</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <div style={{ 
              width: '12px', 
              height: '12px', 
              backgroundColor: '#f44336'
            }} />
            <span>P# = Unfilled Period</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <div style={{ 
              width: '12px', 
              height: '12px', 
              backgroundColor: '#2196f3'
            }} />
            <span>P# = You're Subbing</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Day Details Component
const DayDetails = ({ selectedDate, requests, currentUser, onRequestClick }) => {
  const getActivitiesForDate = (date) => {
    const activities = [];
    
    requests.forEach(request => {
      if (request.dateObj && isSameDay(request.dateObj, date)) {
        request.periods?.forEach(period => {
          const subInfo = request.filled?.[period];
          let status = 'unfilled';
          let substituteInfo = null;
          let isCurrentUser = false;

          if (subInfo && typeof subInfo === 'object' && subInfo.name) {
            status = 'filled';
            substituteInfo = subInfo;
            isCurrentUser = subInfo.email === currentUser.email || subInfo.uid === currentUser.uid;
          } else if (typeof subInfo === 'string' && subInfo.trim() !== "") {
            status = 'filled';
            substituteInfo = { name: subInfo };
          }

          activities.push({
            period: period,
            status: status,
            teacher: request.name,
            department: request.department,
            room: getRoomForPeriod(request, period), // Use new room logic
            requestId: request.id,
            substituteInfo: substituteInfo,
            isCurrentUser: isCurrentUser,
            isMyRequest: request.requesterUid === currentUser.uid,
            fullRequest: request
          });
        });
      }
    });

    return activities.sort((a, b) => Number(a.period) - Number(b.period));
  };

  if (!selectedDate) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '40px', 
        color: '#6c757d',
        border: '1px solid #dee2e6',
        borderRadius: '8px',
        backgroundColor: '#f8f9fa'
      }}>
        Click on a calendar date to view sub request details for that day
      </div>
    );
  }

  const activities = getActivitiesForDate(selectedDate);

  if (activities.length === 0) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '40px', 
        color: '#6c757d',
        border: '1px solid #dee2e6',
        borderRadius: '8px'
      }}>
        No sub requests scheduled for {format(selectedDate, 'EEEE, MMMM d, yyyy')}
      </div>
    );
  }

  // Group activities by teacher/request
  const groupedActivities = activities.reduce((acc, activity) => {
    const key = `${activity.teacher}-${activity.requestId}`;
    if (!acc[key]) {
      acc[key] = {
        teacher: activity.teacher,
        department: activity.department,
        request: activity.fullRequest,
        isMyRequest: activity.isMyRequest,
        requestId: activity.requestId,
        fullRequest: activity.fullRequest,
        periods: []
      };
    }
    acc[key].periods.push(activity);
    return acc;
  }, {});

  const isAdmin = currentUser.hasElevatedPrivileges || currentUser.role === 'HR';

  return (
    <div style={{ 
      border: '1px solid #dee2e6',
      borderRadius: '8px',
      padding: '20px',
      backgroundColor: 'white'
    }}>
      <h3 style={{ 
        margin: '0 0 20px 0',
        color: '#007bff',
        borderBottom: '2px solid #007bff',
        paddingBottom: '10px'
      }}>
        Sub Requests for {format(selectedDate, 'EEEE, MMMM d, yyyy')}
      </h3>

      {Object.values(groupedActivities).map((requestGroup, index) => (
        <div key={`${requestGroup.requestId}-${index}`} style={{
          border: '1px solid #ddd',
          borderRadius: '8px',
          padding: '15px',
          marginBottom: '15px',
          backgroundColor: '#f8f9fa',
          cursor: isAdmin ? 'pointer' : 'default'
        }}
        onClick={() => isAdmin && onRequestClick && onRequestClick(requestGroup.fullRequest)}
        >
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'flex-start',
            marginBottom: '15px'
          }}>
            <div>
              <h4 style={{ margin: '0 0 5px 0' }}>
                {requestGroup.teacher}
                {requestGroup.isMyRequest && (
                  <span style={{fontSize: '0.8em', color: '#007bff', marginLeft: '10px'}}>(Your Request)</span>
                )}
                {isAdmin && (
                  <span style={{fontSize: '0.8em', color: '#28a745', marginLeft: '10px'}}>
                    (Click to view sub plan)
                  </span>
                )}
              </h4>
              <div style={{ fontSize: '0.9em', color: '#666' }}>
                <strong>Department:</strong> {requestGroup.department || 'N/A'} | 
                <strong> Room(s):</strong> {getRoomSummary(requestGroup.request)}
              </div>
            </div>
          </div>

          <div style={{ marginTop: '10px' }}>
            <strong>Periods:</strong>
            <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {requestGroup.periods.map((period, periodIndex) => (
                <div key={`${period.period}-${periodIndex}`} style={{
                  padding: '8px 12px',
                  borderRadius: '20px',
                  fontSize: '0.9em',
                  fontWeight: 'bold',
                  color: 'white',
                  backgroundColor: period.isCurrentUser 
                    ? '#2196f3' 
                    : period.status === 'unfilled' 
                      ? '#f44336' 
                      : '#4caf50',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px'
                }}>
                  <span>Period {period.period}</span>
                  {/* Show room for this period if different rooms are used */}
                  {usesMultipleRooms(requestGroup.request) && (
                    <span style={{ fontSize: '0.8em', opacity: 0.9 }}>
                      ({period.room})
                    </span>
                  )}
                  {period.status === 'unfilled' ? (
                    <span>• Unfilled</span>
                  ) : period.isCurrentUser ? (
                    <span>• You</span>
                  ) : (
                    <span>• {period.substituteInfo?.name || 'Filled'}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const SubbingStatus = (props) => {
  const { currentUser } = props;

  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState('7'); // default 7 days
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('calendar'); // New state for view toggle
  const [selectedDate, setSelectedDate] = useState(null); // New state for selected calendar date
  const [selectedSubRequest, setSelectedSubRequest] = useState(null); // New state for modal
  const [isModalOpen, setIsModalOpen] = useState(false); // New state for modal

  // Handle calendar date click
  const handleDateClick = (date) => {
    setSelectedDate(date);
  };

  // Handle sub request click (for admins)
  const handleRequestClick = (subRequest) => {
    if (currentUser.hasElevatedPrivileges || currentUser.role === 'HR') {
      setSelectedSubRequest(subRequest);
      setIsModalOpen(true);
    }
  };

  // Close modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedSubRequest(null);
  };

  const fetchRequests = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    if (!currentUser || !currentUser.tenantId) {
      console.warn("SubbingStatus: currentUser or tenantId is missing. Cannot fetch requests.");
      setRequests([]);
      setIsLoading(false);
      setError("User or school information is not available to load statuses.");
      return;
    }

    try {
      console.log(`SubbingStatus: Fetching sub_requests for tenant: ${currentUser.tenantId}`);
      
      let allRequestsForUser = [];

      // Check if user has elevated privileges (admin/HR view)
      if (currentUser.hasElevatedPrivileges || currentUser.role === 'HR') {
        // Admin/HR can see all requests for the tenant
        const adminQuery = query(
          collection(db, 'sub_requests'),
          where("tenantId", "==", currentUser.tenantId)
        );
        const adminSnapshot = await getDocs(adminQuery);
        allRequestsForUser = adminSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
      } else {
        // Regular teachers: fetch their own requests + requests they're subbing for
        
        // 1. Get requests where this user is the requester
        const myRequestsQuery = query(
          collection(db, 'sub_requests'),
          where("tenantId", "==", currentUser.tenantId),
          where("requesterUid", "==", currentUser.uid)
        );
        const myRequestsSnapshot = await getDocs(myRequestsQuery);
        const myRequests = myRequestsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        // 2. Get requests where this user is subbing (their email/name is in the 'filled' object)
        const allTenantRequestsQuery = query(
          collection(db, 'sub_requests'),
          where("tenantId", "==", currentUser.tenantId)
        );
        const allTenantSnapshot = await getDocs(allTenantRequestsQuery);
        
        const requestsImSubbingFor = allTenantSnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(request => {
            // Check if this user is subbing for any period in this request
            if (!request.filled) return false;
            
            return Object.values(request.filled).some(subInfo => {
              if (typeof subInfo === 'object' && subInfo.email) {
                return subInfo.email === currentUser.email;
              }
              if (typeof subInfo === 'object' && subInfo.uid) {
                return subInfo.uid === currentUser.uid;
              }
              return false;
            });
          });
        
        // Combine and deduplicate (in case user is both requester and sub for same request)
        const combinedRequests = [...myRequests, ...requestsImSubbingFor];
        const uniqueRequestIds = new Set();
        allRequestsForUser = combinedRequests.filter(request => {
          if (uniqueRequestIds.has(request.id)) {
            return false;
          }
          uniqueRequestIds.add(request.id);
          return true;
        });
      }

      const today = startOfDay(new Date());
      let filteredRequests = [];

      // Process dates for all requests
      const processedRequests = allRequestsForUser.map(d => {
        let dateObj = null;
        if (d.date && typeof d.date === 'string') {
          try {
            dateObj = parseISO(d.date);
            if (isNaN(dateObj.getTime())) {
              console.warn(`Invalid date string for doc ${d.id}: ${d.date}`);
              dateObj = null; 
            }
          } catch (e) {
            console.warn(`Error parsing date for doc ${d.id}: ${d.date}`, e);
            dateObj = null;
          }
        } else if (d.date && typeof d.date.toDate === 'function') {
          dateObj = d.date.toDate();
        } else {
          console.warn(`Missing or unexpected date format for doc ${d.id}:`, d.date);
        }
        return {
          ...d,
          dateObj 
        };
      });

      // Apply date filtering only for list view
      if (viewMode === 'list') {
        switch (filter) {
          case '1':
            const tomorrowStart = addDays(today, 1);
            const dayAfterTomorrowStart = addDays(today, 2);
            filteredRequests = processedRequests.filter(d => d.dateObj && d.dateObj >= tomorrowStart && d.dateObj < dayAfterTomorrowStart);
            break;
          case '7':
            filteredRequests = processedRequests.filter(d => d.dateObj && isAfter(d.dateObj, today) && isBefore(d.dateObj, addDays(today, 8)));
            break;
          case '14':
            filteredRequests = processedRequests.filter(d => d.dateObj && isAfter(d.dateObj, today) && isBefore(d.dateObj, addDays(today, 15)));
            break;
          case 'all':
            filteredRequests = processedRequests.filter(d => d.dateObj && isAfter(d.dateObj, today));
            break;
          default: 
            filteredRequests = processedRequests.filter(d => d.dateObj && isAfter(d.dateObj, today));
        }
      } else {
        // For calendar view, show all future requests
        filteredRequests = processedRequests.filter(d => d.dateObj && isAfter(d.dateObj, today));
      }

      filteredRequests.sort((a, b) => {
          if (a.dateObj && b.dateObj) return a.dateObj - b.dateObj;
          if (a.dateObj) return -1;
          if (b.dateObj) return 1;
          return 0;
      });

      setRequests(filteredRequests);
    } catch (err) {
      console.error("Error fetching subbing status:", err);
      setError("Failed to load subbing statuses. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, filter, viewMode]);

  useEffect(() => {
    if (currentUser && currentUser.tenantId) {
        fetchRequests();
    } else if (currentUser && !currentUser.tenantId) {
        console.warn("SubbingStatus: User is logged in but tenantId is missing. Clearing requests.");
        setRequests([]);
        setIsLoading(false);
        setError("Your school information is not available to load statuses.");
    } else {
        setRequests([]);
        setIsLoading(false);
    }
  }, [fetchRequests]);

  if (isLoading) {
    return <div style={{ textAlign: 'center', marginTop: '50px' }}>Loading subbing statuses...</div>;
  }

  if (error) {
    return <div style={{ textAlign: 'center', marginTop: '50px', color: 'red' }}>{error}</div>;
  }

  // Determine what type of view this is for the header
  const getViewType = () => {
    if (currentUser.hasElevatedPrivileges || currentUser.role === 'HR') {
      return 'All Subbing Requests';
    }
    return 'My Subbing Status';
  };

  const isAdmin = currentUser.hasElevatedPrivileges || currentUser.role === 'HR';

  return (
    <div style={{ maxWidth: '1200px', margin: '20px auto', padding: '0 15px' }}>
      <h1>{getViewType()} for {currentUser?.displayName || currentUser?.name || 'User'}</h1>
      
      {isAdmin && (
        <div style={{
          backgroundColor: '#d1ecf1',
          border: '1px solid #bee5eb',
          borderRadius: '8px',
          padding: '15px',
          marginBottom: '20px'
        }}>
          <strong>Admin View:</strong> You can click on any sub request to view detailed sub plans and attachments.
        </div>
      )}

      {/* View Toggle Buttons */}
      <div style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
        <button
          onClick={() => setViewMode('calendar')}
          style={{
            backgroundColor: viewMode === 'calendar' ? '#007bff' : '#6c757d',
            color: 'white',
            padding: '10px 20px',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer'
          }}
        >
          📅 Calendar View
        </button>
        <button
          onClick={() => setViewMode('list')}
          style={{
            backgroundColor: viewMode === 'list' ? '#007bff' : '#6c757d',
            color: 'white',
            padding: '10px 20px',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer'
          }}
        >
          📋 List View
        </button>
      </div>

      {viewMode === 'calendar' ? (
        <div>
          <SubbingStatusCalendar 
            requests={requests}
            onDateClick={handleDateClick}
            selectedDate={selectedDate}
            currentUser={currentUser}
            onRequestClick={handleRequestClick}
          />
          
          <div style={{ marginTop: '20px' }}>
            <DayDetails 
              selectedDate={selectedDate}
              requests={requests}
              currentUser={currentUser}
              onRequestClick={handleRequestClick}
            />
          </div>
        </div>
      ) : (
        <div>
          <div style={{ marginBottom: '20px' }}>
            <label htmlFor="filter-select" style={{ marginRight: '10px' }}><strong>Filter by future date range:</strong></label>
            <select id="filter-select" value={filter} onChange={e => setFilter(e.target.value)} style={{padding: '8px', borderRadius: '4px'}}>
              <option value="1">Next Day</option>
              <option value="7">Next 7 Days</option>
              <option value="14">Next 14 Days</option>
              <option value="all">All Future</option>
            </select>
          </div>

          {requests.length === 0 && !isLoading ? (
            <p>
              {currentUser.hasElevatedPrivileges || currentUser.role === 'HR' 
                ? "No sub requests found for your school in the selected future date range."
                : "No sub requests found where you are the requester or assigned as a substitute in the selected future date range."
              }
            </p>
          ) : (
            requests.map(req => (
              <div 
                key={req.id} 
                style={{ 
                  border: '1px solid #ddd', 
                  borderRadius: '8px', 
                  padding: '16px', 
                  marginBottom: '16px', 
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                  cursor: isAdmin ? 'pointer' : 'default',
                  transition: 'box-shadow 0.2s'
                }}
                onClick={() => isAdmin && handleRequestClick(req)}
                onMouseEnter={(e) => {
                  if (isAdmin) {
                    e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (isAdmin) {
                    e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                  }
                }}
              >
                <h3 style={{marginTop: 0, marginBottom: '10px'}}>
                  {req.name} <span style={{fontWeight: 'normal', color: '#555'}}>({req.department || 'N/A'})</span>
                  {req.requesterUid === currentUser.uid && (
                    <span style={{fontSize: '0.8em', color: '#007bff', marginLeft: '10px'}}>(Your Request)</span>
                  )}
                  {isAdmin && (
                    <span style={{fontSize: '0.8em', color: '#28a745', marginLeft: '10px'}}>
                      (Click to view sub plan)
                    </span>
                  )}
                </h3>
                <p><strong>Date:</strong> {req.dateObj ? format(req.dateObj, 'EEEE, MMMM d, yyyy') : (req.date || 'Date not specified')}</p>
                <p><strong>Room(s):</strong> {getRoomSummary(req)}</p>
                <p><strong>Status by Period:</strong></p>
                <ul style={{ listStyleType: 'none', paddingLeft: '0', marginTop: '5px' }}>
                  {(req.periods && Array.isArray(req.periods) && req.periods.length > 0) ? (
                    req.periods.sort((a,b) => Number(a) - Number(b)).map(period => {
                      const subInfo = req.filled?.[period];
                      let displayStatus;
                      let isUnfilled = true;
                      let isCurrentUser = false;

                      if (subInfo && typeof subInfo === 'object' && subInfo.name) {
                        displayStatus = subInfo.name;
                        isUnfilled = false;
                        // Check if this is the current user subbing
                        isCurrentUser = subInfo.email === currentUser.email || subInfo.uid === currentUser.uid;
                      } else if (typeof subInfo === 'string' && subInfo.trim() !== "") { 
                        displayStatus = `Taken (Legacy: ${subInfo})`;
                        isUnfilled = false;
                      } else {
                        displayStatus = 'Unfilled';
                        isUnfilled = true;
                      }

                      // Get room for this specific period
                      const periodRoom = getRoomForPeriod(req, period);
                      const roomDisplay = usesMultipleRooms(req) ? ` (${periodRoom})` : '';

                      return (
                        <li 
                          key={period} 
                          style={{ 
                            padding: '5px 0',
                            color: isUnfilled ? '#dc3545' : (isCurrentUser ? '#007bff' : '#28a745'),
                            fontWeight: isUnfilled ? 'normal' : 'bold' 
                          }}
                        >
                          Period {period}{roomDisplay}: {displayStatus}
                          {isCurrentUser && <span style={{fontSize: '0.9em'}}> (You)</span>}
                        </li>
                      );
                    })
                  ) : (
                    <li style={{fontStyle: 'italic', color: '#777'}}>No periods listed for this request.</li>
                  )}
                </ul>
              </div>
            ))
          )}
        </div>
      )}

      {/* Sub Plan Modal */}
      <SubPlanModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        subRequest={selectedSubRequest}
        currentUser={currentUser}
      />
    </div>
  );
};

export default SubbingStatus;